import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useAuthStore } from "@/auth/authStore";
import { hasGlobalRole } from "@/auth/permissions";
import { useLanguage } from "@/i18n/LanguageContext";
import { DaaruLogo } from "./branding/DaaruLogo";
import { FingletekCredit } from "./branding/FingletekCredit";
import { AdminShell } from "./shells/AdminShell";
import { LanguageSwitcher } from "./shells/LanguageSwitcher";
import { ParticipantShell } from "./shells/ParticipantShell";
import "./theme.css";

function isParticipantAppPath(pathname: string): boolean {
  return (
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/submit-paper") ||
    pathname.startsWith("/reviews") ||
    pathname.startsWith("/account") ||
    pathname === "/schedule" ||
    pathname === "/venue"
  );
}

function PublicShell() {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const clear = useAuthStore((s) => s.clear);
  const isAdmin = hasGlobalRole(globalRoles, "admin");

  return (
    <div className="dn-app" dir={dir}>
      <a className="dn-skip-link" href="#dn-main-content">
        {t.common.skipToContent}
      </a>
      <div className="dn-shell">
        <header className="dn-header">
          <div className="dn-header-inner">
            <Link to="/" className="dn-brand">
              <DaaruLogo size="md" alt={t.brandShort} />
              <div className="dn-brand-text">
                <strong>{t.brandShort}</strong>
                <span>{t.conferenceEdition}</span>
              </div>
            </Link>
            <nav className="dn-nav" aria-label={t.common.mainNav}>
              <NavLink to="/" end>
                {t.nav.home}
              </NavLink>
              <NavLink to="/call-for-papers">{t.nav.cfp}</NavLink>
              <NavLink to="/papers">{t.nav.papers}</NavLink>
              <NavLink to="/past-events">{t.nav.pastEvents}</NavLink>
              <NavLink to="/schedule">{t.nav.schedule}</NavLink>
              <NavLink to="/venue">{t.nav.venue}</NavLink>
              {!accessToken && <NavLink to="/register">{t.nav.register}</NavLink>}
              {!accessToken && <NavLink to="/login">{t.nav.login}</NavLink>}
              {accessToken && !isAdmin && <NavLink to="/dashboard">{t.nav.dashboard}</NavLink>}
              {isAdmin && <NavLink to="/admin">{t.nav.admin}</NavLink>}
              <LanguageSwitcher />
              {accessToken && (
                <button
                  type="button"
                  className="dn-btn dn-btn-ghost"
                  onClick={() => {
                    clear();
                    navigate("/");
                  }}
                >
                  {t.nav.logout}
                </button>
              )}
            </nav>
          </div>
        </header>
        <main id="dn-main-content" className="dn-main">
          <div className="dn-route-fade">
            <Outlet />
          </div>
        </main>
        <footer className="dn-footer">
          <p>{t.brandOrg}</p>
          <p>{t.conferenceEdition}</p>
          <FingletekCredit />
        </footer>
      </div>
    </div>
  );
}

export function ConferenceLayout() {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const globalRoles = useAuthStore((s) => s.globalRoles);
  const isAdmin = hasGlobalRole(globalRoles, "admin");
  const path = location.pathname;

  if (
    accessToken &&
    user?.must_change_password &&
    path !== "/set-password" &&
    path !== "/login"
  ) {
    return <Navigate to="/set-password" replace />;
  }

  if (accessToken && isAdmin && (path.startsWith("/admin") || path.startsWith("/account"))) {
    return (
      <div className="dn-app">
        <AdminShell />
      </div>
    );
  }

  if (accessToken && !isAdmin && isParticipantAppPath(path)) {
    return (
      <div className="dn-app">
        <ParticipantShell />
      </div>
    );
  }

  return <PublicShell />;
}
